import { ApiGraphqlTypes as AGT, nzApiClient } from '@nizza/core';
import {
  Collection,
  CollectionInput,
  CollectionRepository,
  CollectionSearchInput,
  CollectionUpdateInput,
  ProductToSync,
} from '../domain';
import { createCollection } from '../../utils';

export class CollectionGraphQLRepository implements CollectionRepository {
  async find(account: string, id: string): Promise<Collection | null> {
    const variables = { data: { account, id } };
    const { findCollection } = await nzApiClient.graphql(
      AGT.FindCollectionDocument,
      variables,
    );
    const collectionData = this.getSuccessData(findCollection);
    return collectionData
      ? this.toDomain(collectionData as AGT.Collection)
      : null;
  }

  async search(input: CollectionSearchInput): Promise<Collection[]> {
    const variables = { data: input };
    const { searchCollection } = await nzApiClient.graphql(
      AGT.SearchCollectionsDocument,
      variables,
      { secure: true, account: input.account },
    );
    const collectionsData = this.getSuccessData(searchCollection);
    return collectionsData?.map(c => this.toDomain(c as AGT.Collection)) ?? [];
  }

  async getAll(input: CollectionSearchInput): Promise<Collection[]> {
    const variables = { data: input };
    const { getAllCollection } = await nzApiClient.graphql(
      AGT.GetAllCollectionsDocument,
      variables,
      { secure: true, account: input.account },
    );
    const collectionsData = this.getSuccessData(getAllCollection);
    return collectionsData?.map(c => this.toDomain(c as AGT.Collection)) ?? [];
  }

  async create(input: CollectionInput): Promise<Collection> {
    const variables = { data: input as AGT.CreateCollectionInput };
    const { createCollection } = await nzApiClient.graphql(
      AGT.AddCollectionDocument,
      variables,
      { secure: true, account: input.account },
    );
    const collectionData = this.getSuccessData(createCollection);
    if (!collectionData) {
      throw new Error('Collection creation failed for an unknown reason.');
    }
    return this.toDomain(collectionData as AGT.Collection);
  }

  async update(input: CollectionUpdateInput): Promise<void> {
    const variables = { data: input as AGT.UpdateCollectionInput };
    const { updateCollection } = await nzApiClient.graphql(
      AGT.UpdateCollectionDocument,
      variables,
      { secure: true, account: input.account },
    );
    if (!this.isSuccess(updateCollection)) {
      throw new Error('Collection update failed for an unknown reason.');
    }
  }

  async delete(account: string, id: string): Promise<void> {
    const variables = { data: { account, id } };
    const { deleteCollection } = await nzApiClient.graphql(
      AGT.DeleteCollectionDocument,
      variables,
      { secure: true, account },
    );
    if (!this.isSuccess(deleteCollection)) {
      throw new Error('Collection deletion failed for an unknown reason.');
    }
  }

  async manageProduct(data: ProductToSync): Promise<boolean> {
    const variables = { data: data as AGT.ManageCollectionProductInInput };
    const { manageCollectionProduct } = await nzApiClient.graphql(
      AGT.ManageCollectionProductDocument,
      variables,
      { secure: true, account: data.account },
    );
    return this.isSuccess(manageCollectionProduct);
  }

  private toDomain(raw: AGT.Collection | CollectionInput): Collection {
    return createCollection(raw);
  }

  private throwOnGraphQLError(
    payload: { __typename?: string; message?: string } | null | undefined,
  ) {
    if (payload?.__typename === 'ErrorGeneral') {
      throw new Error(
        payload.message || 'A GraphQL operation returned an error.',
      );
    }
  }

  private getSuccessData<T>(
    payload:
      | { __typename?: string; message?: string; data?: T | null }
      | null
      | undefined,
  ): T | null {
    this.throwOnGraphQLError(payload);
    if (payload?.__typename?.includes('Success') && payload.data) {
      return payload.data;
    }
    return null;
  }

  private isSuccess(
    payload: { __typename?: string; message?: string } | null | undefined,
  ): boolean {
    this.throwOnGraphQLError(payload);
    return payload?.__typename?.includes('Success') ?? false;
  }
}
